// Composer — faithful to Reputifly AI Local App.tsx:4064-4571.
// Auto-resize textarea (≤200px). + attach (left, perfect circle).
// During streaming, render BOTH pause AND send buttons (right) so
// the user can interrupt the current reply OR enqueue follow-ups.
// Send button is white circle with custom up-arrow SVG (NOT pink
// gradient). Slash-key still opens the skill menu (no separate /
// button — keyboard-only entry to match AI Local minimalism).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Maximize2, Minimize2, Plus } from "react-feather";
import "../styles/ops-build-polish.css";
import { EFFORT_LABEL, shownEffort } from "../lib/effortLevel";
import ThinkingLevelMenu from "./ThinkingLevelMenu";
import { useSession } from "../store/session";
import AttachmentChip from "./AttachmentChip";
import ImageAnnotator from "./ImageAnnotator";
import type { AttachItem } from "../lib/types";
import DelegateRunBadge from "./DelegateRunBadge";
import DelegateEffortPicker from "./DelegateEffortPicker";
import { clearDelegateDraft, delegateDraftOptions, setDelegateDraftEffort, shouldOpenDelegateEffortPicker } from "../lib/delegatePresentation";
import ApprovalBanner from "./ApprovalBanner";
import ReviewText from "./ReviewText";
import { appendReviewReference, formatReviewDraft, parseReviewDraft, type ReviewReference } from "../lib/reviewReferences";
import ClientProposalConfigurator from "./ClientProposalConfigurator";
import ContextMeter from "./ContextMeter";
import SlashMenu from "./SlashMenu";
import TagMenu, { SiteFavicon } from "./TagMenu";
import { cachedSiteTags, filterSiteTags, loadSiteTags, onSiteTagsChange, tagDisplayName, tagsInText, type SiteTag } from "../lib/siteTags";
import WorkflowPicker from "./WorkflowPicker";
import ComposerTools from "./ComposerTools";
import { appendWorkflowDraft, workflowDraftSnapshot } from "../lib/composerWorkflows";
import WhatsAppImportSheet from "./WhatsAppImportSheet";
import { filterSkills, loadLiveSkills, type SkillDescriptor } from "../lib/skills";
import { authFetch } from "../auth-context";
import { useIsPhone } from "../lib/usePhoneLayout";
import {
  DEFAULT_CLIENT_PROPOSAL_OPTIONS,
  formatClientProposalDraft,
  parseClientProposalDraft,
  stripTypedClientProposalAlias,
  type ClientProposalOptions,
} from "../lib/clientProposal";

// Thinking-level chip (9 Oct 2026, same as Build): the chip opens a menu of
// Medium / High / xHigh / Max for the ACTIVE chat — names and a check only.
// Per-chat: the level is stored on the company (effortLevel) and applied as the
// spawn effort. Default is xHigh when a chat has none set. A stored "low" still
// reads Low; a legacy "ultra" (the retired Ultracode tier) reads and acts as Max.

// Creative sample commands only make sense in a Sample chat (the backend 409s
// them elsewhere), so the slash popup hides them in client chats.
const SAMPLE_ONLY_SKILLS = new Set(["codex-delegate-sample-creative", "opus-sample-creative", "recycle-sample"]);

const TEXTAREA_MIN_PX = 26;
const TEXTAREA_MAX_PX = 200;

/**
 * Kill switch for the WhatsApp export importer. Flip to false and ship a
 * frontend-only release to remove the button entirely — no backend involved,
 * so no restart and no session is disturbed.
 */
const WHATSAPP_IMPORT_ENABLED = true;

export default function Composer() {
  // Load the box's REAL skill list once, so /gen-video (and anything added
  // later) shows in the slash popup without hand-editing lib/skills.ts.
  useEffect(() => { void loadLiveSkills(authFetch); }, []);
  const activeCompanyId = useSession((s) => s.activeCompanyId);
  return <CompanyComposer key={activeCompanyId ?? "no-company"} />;
}

function CompanyComposer() {
  const rawDraft = useSession((s) => s.composerDraft);
  const reviewDraft = useMemo(() => parseReviewDraft(rawDraft), [rawDraft]);
  const delegateDraft = delegateDraftOptions(rawDraft, "operations");
  const [delegatePickerRequest, setDelegatePickerRequest] = useState(0);
  const setDraft = useSession((s) => s.setComposerDraft);
  const attachments = useSession((s) => s.attachments);
  const addAttachment = useSession((s) => s.addAttachmentFromFile);
  const removeAttachment = useSession((s) => s.removeAttachment);
  const replaceAttachmentImage = useSession((s) => s.replaceAttachmentImage);
  // The pending image open in the draw-on-image view (null = closed). This composer is keyed
  // by chat, so switching chats closes it.
  const [annotating, setAnnotating] = useState<AttachItem | null>(null);
  const closeAnnotate = useCallback(() => setAnnotating(null), []);
  const sendMessage = useSession((s) => s.sendMessage);
  const interruptStream = useSession((s) => s.interruptStream);
  const streamingMessageId = useSession((s) => s.streamingMessageId);
  const queueByCompany = useSession((s) => s.queueByCompany);
  const activeCompanyId = useSession((s) => s.activeCompanyId);
  const setLightbox = useSession((s) => s.setLightbox);
  const setHoverPreview = useSession((s) => s.setHoverPreview);
  const isSampleChat = useSession((s) => s.companies.some((c) => c.id === s.activeCompanyId && c.kind === "sample"));
  const creativeScopeInvalid = delegateDraft?.sampleProfile === "creative" && !isSampleChat;
  // Per-chat reasoning effort: read from the ACTIVE company (default xHigh).
  const thinkingLevel = useSession((s) => {
    const c = s.companies.find((x) => x.id === s.activeCompanyId);
    return shownEffort(c?.effortLevel);
  });
  const setThinkingLevel = useSession((s) => s.setThinkingLevel);
  // The chip that opened the thinking-level menu (null = closed); the menu anchors to it.
  const [thinkAnchor, setThinkAnchor] = useState<HTMLElement | null>(null);
  const thinkMenuOpen = !!thinkAnchor;
  const closeThinkMenu = useCallback(() => setThinkAnchor(null), []);
  const notifyOnDoneArmed = useSession((s) => s.notifyOnDoneArmed);
  const setNotifyOnDoneArmed = useSession((s) => s.setNotifyOnDoneArmed);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Phones: stops the "keep the bar above the keyboard" follow started on focus.
  const kbFollowRef = useRef<(() => void) | null>(null);
  useEffect(() => () => kbFollowRef.current?.(), []);
  const formRef = useRef<HTMLFormElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const barHeight = useRef(0);
  // Phones (29 Sep 2026): the bar rests as one slim glass pill (+ · input ·
  // context · send) and opens into the full bar on tap. Desktop never collapses.
  const isPhone = useIsPhone();
  const [focusInside, setFocusInside] = useState(false);
  const highlightRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const waInputRef = useRef<HTMLInputElement>(null);
  const [isDropping, setIsDropping] = useState(false);
  // Long messages (29 Sep): an expand control at the top right of the bar lets the SAME
  // field grow to most of the screen for editing (no second editor, so the draft,
  // references and send path stay exactly as they are). It resets after sending.
  const [expanded, setExpanded] = useState(false);
  const [tall, setTall] = useState(false);
  const [mobileReturn, setMobileReturn] = useState(() => window.matchMedia("(pointer: coarse), (max-width: 640px)").matches);
  useEffect(() => {
    const query = window.matchMedia("(pointer: coarse), (max-width: 640px)");
    const update = () => setMobileReturn(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  // The picked .zip. Non-null mounts the import sheet; the sheet owns every
  // bit of state from there and hands its result back through the ordinary
  // attachment action, so nothing here needs to know about WhatsApp.
  const [waFile, setWaFile] = useState<File | null>(null);

  // Slash-menu state
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashQuery, setSlashQuery] = useState("");
  const [slashAnchor, setSlashAnchor] = useState<number>(-1);
  const [slashSelectedIndex, setSlashSelectedIndex] = useState(0);
  // @ site tags (16 Oct 2026): `@` at a word start opens the website picker.
  const [tagOpen, setTagOpen] = useState(false);
  const [tagQuery, setTagQuery] = useState("");
  const [tagAnchor, setTagAnchor] = useState(-1);
  const [tagIndex, setTagIndex] = useState(0);
  const [siteTags, setSiteTags] = useState<SiteTag[]>(() => cachedSiteTags());
  const [siteTagsLoading, setSiteTagsLoading] = useState(false);
  useEffect(() => {
    const off = onSiteTagsChange(() => setSiteTags(cachedSiteTags()));
    setSiteTagsLoading(true);
    void loadSiteTags(authFetch).then((t) => setSiteTags(t)).finally(() => setSiteTagsLoading(false));
    return off;
  }, []);
  const filteredTags = useMemo(() => (tagOpen ? filterSiteTags(siteTags, tagQuery) : []), [tagOpen, siteTags, tagQuery]);
  const [proposalEditor, setProposalEditor] =
    useState<ClientProposalOptions | null>(null);

  // A confirmed proposal remains an ordinary composer command in the store.
  // That deliberately preserves the existing queue, retry, and failure-restore
  // behavior without adding any proposal-specific state to the session layer.
  const lockedProposal = useMemo(
    () => parseClientProposalDraft(reviewDraft.text),
    [reviewDraft.text],
  );
  const draft = lockedProposal?.note ?? reviewDraft.text;

  const queueLength = activeCompanyId
    ? (queueByCompany[activeCompanyId]?.length ?? 0)
    : 0;
  // Server-side busy flag (truth) — bumped via /all-status poll every 6s.
  // We OR with local streamingMessageId so the Stop button is reachable
  // even when local state lost track (PWA backgrounded / cold restart /
  // SSE dropped) but the server still says claude is processing a turn.
  // Julian: "if the session is running I must be able to interrupt".
  const sessionStatusByCompany = useSession((s) => s.sessionStatusByCompany);
  const serverBusy =
    !!activeCompanyId &&
    sessionStatusByCompany[activeCompanyId]?.busy === true;
  const isStreaming = !!streamingMessageId || serverBusy;
  const hasReadyAttachment = attachments.some((a) => a.status === "ready");
  const hasCompressing = attachments.some((a) => a.status === "compressing");
  const canSend =
    !!activeCompanyId &&
    !delegateDraft?.invalid &&
    !creativeScopeInvalid &&
    !hasCompressing &&
    !proposalEditor &&
    (rawDraft.trim().length > 0 || hasReadyAttachment);

  // The slim bar never hides anything you are in the middle of: typed text,
  // attachments, a proposal, the slash list, the WhatsApp importer or a
  // highlighted reference all keep it open. Armed extras (Astra, notify) may
  // rest closed; a dot on + says something is armed.
  // Julian (29 Sep night): on phones the full bar (+ · Tools · effort · context · send)
  // shows at rest, like Build. Set to true to bring back the slim resting pill.
  const COLLAPSE_AT_REST = false;
  const collapsed = COLLAPSE_AT_REST && isPhone && !focusInside && !rawDraft.trim() && !draft.trim()
    && attachments.length === 0 && !proposalEditor && !lockedProposal && !slashOpen
    && !waFile && reviewDraft.references.length === 0;
  const armed = !!delegateDraft || notifyOnDoneArmed;

  const filteredSkills = useMemo(
    () => (slashOpen ? filterSkills(slashQuery) : filterSkills(""))
      .filter((skill) => isSampleChat || !SAMPLE_ONLY_SKILLS.has(skill.name)),
    [slashOpen, slashQuery, isSampleChat],
  );

  // Phones: the chat scrolls UNDER the floating glass bar, so the message list
  // reserves exactly the bar's height (--m-composer-h, read by mobile-glass.css).
  useEffect(() => {
    const el = formRef.current;
    if (!isPhone || !el || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--m-composer-h", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => { observer.disconnect(); root.style.removeProperty("--m-composer-h"); };
  }, [isPhone]);

  // Open/close of the phone bar. It opens when anything in it takes focus
  // (tapping the text field). It closes when focus really leaves or you tap
  // somewhere else — never because you tapped one of its own buttons (iOS
  // blurs the field on a button tap) or a popover it opened (Tools, workflows,
  // context, Astra effort are portalled outside the form).
  const pointerInsideAt = useRef(0);
  const insideBar = useCallback((node: Node | null) => {
    if (!node) return false;
    if (formRef.current?.contains(node)) return true;
    const el = node instanceof Element ? node : node.parentElement;
    return !!el?.closest(".composer-tools__menu, .wf-overlay, .contextMeter__popover, .astraSheet, .slashMenu, dialog, [role='dialog'], [role='menu']");
  }, []);
  function onFormBlur() {
    window.setTimeout(() => {
      if (Date.now() - pointerInsideAt.current < 600) return;
      const active = document.activeElement;
      setFocusInside(!!active && active !== document.body && insideBar(active));
    }, 150);
  }
  useEffect(() => {
    if (!isPhone || !focusInside) return;
    const onPointerDown = (event: PointerEvent) => {
      // Scrolling the chat with the keyboard up keeps the field focused on iOS:
      // the bar stays open while the field has focus.
      if (document.activeElement === textareaRef.current) return;
      if (!insideBar(event.target as Node | null)) setFocusInside(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [isPhone, focusInside, insideBar]);
  // Phones: opening/closing the bar glides instead of jumping. The bar's
  // height is tracked continuously; when it switches between the slim pill and
  // the full bar, it animates from the old height to the new one (before the
  // browser paints the new layout), while the controls fade in (CSS).
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!isPhone || !el || typeof ResizeObserver === "undefined") return;
    barHeight.current = el.getBoundingClientRect().height;
    const observer = new ResizeObserver(() => { barHeight.current = el.getBoundingClientRect().height; });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isPhone]);
  const lastCollapsed = useRef(collapsed);
  useLayoutEffect(() => {
    const el = barRef.current;
    if (lastCollapsed.current === collapsed) return;
    lastCollapsed.current = collapsed;
    if (!isPhone || !el || typeof el.animate !== "function") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const from = barHeight.current, to = el.getBoundingClientRect().height;
    if (!from || Math.abs(from - to) < 2) return;
    el.animate(
      [{ height: `${from}px`, overflow: "hidden" }, { height: `${to}px`, overflow: "hidden" }],
      { duration: 280, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
    );
  }, [collapsed, isPhone]);

  // iOS standalone can leave the page scrolled up by the keyboard's height
  // after it closes, so the bar floats with an empty band beneath it. Whenever
  // the keyboard is gone (visual viewport back to full height), put the page back.
  useEffect(() => {
    if (!isPhone) return;
    const vv = window.visualViewport;
    const settle = () => {
      // Keyboard still up (the field has focus): never undo the lift, whatever
      // innerHeight reports on this iOS version.
      if (document.activeElement === textareaRef.current) return;
      if (vv && vv.height < window.innerHeight - 2) return;
      if (window.scrollY !== 0) window.scrollTo(0, 0);
      // scrollIntoView can also nudge the fixed-height app containers; put them back too.
      document.querySelectorAll<HTMLElement>(".shell, .chat").forEach((el) => { if (el.scrollTop) el.scrollTop = 0; });
    };
    const onFocusOut = () => window.setTimeout(settle, 350);
    vv?.addEventListener("resize", settle);
    document.addEventListener("focusout", onFocusOut);
    return () => { vv?.removeEventListener("resize", settle); document.removeEventListener("focusout", onFocusOut); };
  }, [isPhone]);

  // After a send the phone keyboard drops (iOS blurs the field on a button
  // tap); settle the bar back to its slim rest unless the field kept focus.
  function settleAfterSend() {
    if (!isPhone) return;
    window.setTimeout(() => { if (document.activeElement !== textareaRef.current) setFocusInside(false); }, 350);
  }

  // Auto-resize textarea.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = expanded ? Math.max(TEXTAREA_MAX_PX, Math.round(window.innerHeight * 0.6)) : TEXTAREA_MAX_PX;
    const next = Math.min(
      max,
      Math.max(TEXTAREA_MIN_PX, el.scrollHeight),
    );
    el.style.height = `${next}px`;
    setTall(el.scrollHeight > TEXTAREA_MAX_PX);
  }, [draft, collapsed, expanded]);
  // Sent or cleared: back to the normal bar.
  useEffect(() => { if (!draft.trim() && expanded) setExpanded(false); }, [draft, expanded]);
  // A taken-back queued image is already uploaded (serverPath, no local copy): fetch it once so its
  // chip shows the picture, not a file icon (12 Oct 2026). The words and the file are already safe.
  useEffect(() => {
    const missing = attachments.filter(a => a.kind === "image" && a.status === "ready" && a.serverPath && !a.blobUrl);
    if (!missing.length) return;
    let live = true;
    for (const a of missing) {
      const parts = (a.serverPath ?? "").split("/");
      if (parts.length < 2) continue;
      void authFetch(`/api/uploads/${encodeURIComponent(parts.at(-2)!)}/${encodeURIComponent(parts.at(-1)!)}`)
        .then(r => r.ok ? r.blob() : null)
        .then(blob => {
          if (!blob || !live) return;
          const url = URL.createObjectURL(blob);
          useSession.setState(s => ({ attachments: s.attachments.map(x => x.id === a.id && !x.blobUrl ? { ...x, blobUrl: url } : x) }));
        })
        .catch(() => { /* the chip keeps its file icon */ });
    }
    return () => { live = false; };
  }, [attachments]);
  // A queued message taken back (X) lands here: put the caret at the end, ready to edit and send.
  useEffect(() => {
    const focus = () => requestAnimationFrame(() => {
      const el = textareaRef.current; if (!el) return;
      el.focus({ preventScroll: true }); el.setSelectionRange(el.value.length, el.value.length);
    });
    window.addEventListener("composer-focus", focus);
    return () => window.removeEventListener("composer-focus", focus);
  }, []);

  function setVisibleDraft(value: string) {
    // Fresh user invocation only: never reopen from queue/history restoration.
    if (!lockedProposal && shouldOpenDelegateEffortPicker(rawDraft, value, "operations")) {
      setDelegatePickerRequest(current => current + 1);
      setSlashOpen(false);
    }
    setDraft(formatReviewDraft(
      lockedProposal
        ? formatClientProposalDraft(lockedProposal.options, value)
        : value,
      reviewDraft.references,
    ));
  }

  async function insertReference(reference: ReviewReference) {
    const state = useSession.getState();
    if (!activeCompanyId || state.activeCompanyId !== activeCompanyId) return;
    // All Chats is a review surface, not a shared client conversation. Always
    // discuss in the item's own chat, using normal navigation (including its
    // unsent draft/attachment confirmation) before inserting the reference.
    let target = activeCompanyId;
    if (reference.companyId !== activeCompanyId) {
      target = reference.companyId;
      await state.selectCompany(target);
      if (useSession.getState().activeCompanyId !== target) return;
    }
    const current = useSession.getState();
    current.setComposerDraft(appendReviewReference(current.composerDraft, reference));
    setSlashOpen(false);
    requestAnimationFrame(() => {
      const input = textareaRef.current;
      if (!input || useSession.getState().activeCompanyId !== target) return;
      input.focus({ preventScroll: true });
      input.setSelectionRange(input.value.length, input.value.length);
      input.scrollTop = input.scrollHeight;
      if (highlightRef.current) highlightRef.current.scrollTop = input.scrollTop;
    });
  }

  /** Pre-fill an owner /exchange hand-over in the item's own chat. Never sends: the
   * owner's own Send is the /exchange invocation the server requires. Asks before
   * replacing an unsent draft. */
  async function handOverAsExchange(companyId: string, text: string) {
    const state = useSession.getState();
    if (!activeCompanyId || state.activeCompanyId !== activeCompanyId) return;
    if (companyId !== activeCompanyId) {
      await state.selectCompany(companyId);
      if (useSession.getState().activeCompanyId !== companyId) return;
    }
    const current = useSession.getState();
    if (current.composerDraft.trim() && current.composerDraft !== text
      && !window.confirm("Replace your unsent message with the Exchange hand-over?")) return;
    current.setComposerDraft(text);
    setSlashOpen(false);
    requestAnimationFrame(() => {
      const input = textareaRef.current;
      if (!input || useSession.getState().activeCompanyId !== companyId) return;
      input.focus({ preventScroll: true });
      input.setSelectionRange(input.value.length, input.value.length);
    });
  }

  function getWorkflowDraft() {
    const state = useSession.getState();
    if (!activeCompanyId || state.activeCompanyId !== activeCompanyId) return "";
    return workflowDraftSnapshot(parseReviewDraft(state.composerDraft).text, reviewDraft.text, textareaRef.current?.value).visible;
  }

  function insertWorkflow(prompt: string) {
    // Read the current chat/draft synchronously, then use the app's normal
    // setter so existing per-chat persistence also receives the inserted text.
    // Never replace a selected range or touch attachments / composer options.
    const state = useSession.getState();
    if (!activeCompanyId || state.activeCompanyId !== activeCompanyId) return;
    const visibleValue = textareaRef.current?.value;
    const currentReview = parseReviewDraft(state.composerDraft);
    state.setComposerDraft(formatReviewDraft(appendWorkflowDraft(currentReview.text, reviewDraft.text, prompt, visibleValue), currentReview.references));
    setSlashOpen(false);
    requestAnimationFrame(() => {
      if (useSession.getState().activeCompanyId !== activeCompanyId) return;
      const el = textareaRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      el.setSelectionRange(el.value.length, el.value.length);
      el.scrollTop = el.scrollHeight;
    });
  }

  function openScheduledTools() {
    const state = useSession.getState();
    if (!activeCompanyId || state.activeCompanyId !== activeCompanyId) return;
    setSlashOpen(false);
    state.openScheduled();
  }

  function openClientProposal(
    note: string,
    options: ClientProposalOptions = DEFAULT_CLIENT_PROPOSAL_OPTIONS,
  ) {
    setDraft(formatReviewDraft(note, reviewDraft.references));
    setProposalEditor({ ...options });
    setSlashOpen(false);
    // The next action is a tap, so dismiss the mobile keyboard immediately.
    requestAnimationFrame(() => textareaRef.current?.blur());
  }

  function cancelClientProposal() {
    if (lockedProposal) setDraft(formatReviewDraft(lockedProposal.note, reviewDraft.references));
    setProposalEditor(null);
    setSlashOpen(false);
  }

  function confirmClientProposal() {
    if (!proposalEditor) return;
    setDraft(formatReviewDraft(formatClientProposalDraft(proposalEditor, reviewDraft.text), reviewDraft.references));
    setProposalEditor(null);
    setSlashOpen(false);
  }

  function editClientProposal() {
    if (!lockedProposal) return;
    openClientProposal(lockedProposal.note, lockedProposal.options);
  }

  // Slash detection. The input handler passes the just-typed value directly so
  // filtering never lags one render behind the visible text.
  const updateSlashFromValue = useCallback((value: string, pos: number) => {
    if (proposalEditor) {
      setSlashOpen(false);
      return;
    }
    const upToCursor = value.slice(0, pos);
    let anchor = -1;
    for (let i = upToCursor.length - 1; i >= 0; i--) {
      const ch = upToCursor[i];
      if (
        ch === "/" &&
        (i === 0 || /\s/.test(upToCursor[i - 1] ?? ""))
      ) {
        anchor = i;
        break;
      }
      if (/\s/.test(ch)) break;
    }
    if (anchor === -1) {
      setSlashOpen(false);
      return;
    }
    const q = upToCursor.slice(anchor + 1);
    if (q.length > 40) {
      setSlashOpen(false);
      return;
    }
    setSlashOpen(true);
    setSlashQuery(q);
    setSlashAnchor(anchor);
    setSlashSelectedIndex(0);
  }, [proposalEditor]);

  // @ detection: an `@` at a word start up to the cursor, followed by a handle-ish query.
  const updateTagFromValue = useCallback((value: string, pos: number) => {
    if (proposalEditor) { setTagOpen(false); return; }
    const upToCursor = value.slice(0, pos);
    const m = /(^|[\s([{,;:"'])@([a-z0-9-]{0,40})$/i.exec(upToCursor);
    if (!m) { setTagOpen(false); return; }
    setTagOpen(true);
    setSlashOpen(false);
    setTagQuery(m[2] ?? "");
    setTagAnchor(upToCursor.length - (m[2] ?? "").length - 1);
    setTagIndex(0);
  }, [proposalEditor]);

  // Read the LIVE textarea value, not the `draft` state. They are normally the
  // same, but only onChange keeps them in sync — so any path that changes the
  // field without firing React's onChange (a programmatic `.value =`, some
  // autofill/dictation/IME paths) leaves `draft` behind while the user is
  // looking at different text. Recomputing from state would then re-derive the
  // query from the stale string and the popup would disagree with the field.
  // The DOM is what the user can actually see, so trust it here.
  const recomputeSlash = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    updateSlashFromValue(el.value, el.selectionStart ?? 0);
    updateTagFromValue(el.value, el.selectionStart ?? 0);
  }, [updateSlashFromValue, updateTagFromValue]);

  // Enter / Send trust the visible field too: when a programmatic `.value =`,
  // autofill or a dictation path left the stored draft behind (see
  // recomputeSlash), copy the field into the draft first so the text the
  // user sees is what gets sent instead of a silent no-op.
  function adoptVisibleDraft(): boolean {
    const el = textareaRef.current;
    if (!el || !activeCompanyId || lockedProposal || proposalEditor || el.value === draft) return false;
    if (hasCompressing || !el.value.trim()) return false;
    // Same validity gates as canSend, judged on the text that will be sent.
    const next = formatReviewDraft(el.value, reviewDraft.references);
    const nextDelegate = delegateDraftOptions(next, "operations");
    if (nextDelegate?.invalid || (nextDelegate?.sampleProfile === "creative" && !isSampleChat)) return false;
    setDraft(next);
    return true;
  }

  function pickSkill(skill: SkillDescriptor) {
    const el = textareaRef.current;
    if (!el || slashAnchor < 0) {
      setSlashOpen(false);
      return;
    }
    const pos = el.selectionStart ?? 0;
    const before = draft.slice(0, slashAnchor);
    const after = draft.slice(pos);
    if (skill.name === "client-proposal") {
      openClientProposal(before + after);
      return;
    }
    // /notify-julian is meta — don't insert text. Just arm the flag for the next
    // send. Picking it again disarms (toggle behavior). (/notify-farhan and
    // /notify-all are NOT meta — they insert text so the agent runs the box skill.)
    if (skill.name === "notify-julian") {
      setVisibleDraft(before + after);
      setSlashOpen(false);
      setNotifyOnDoneArmed(!notifyOnDoneArmed);
      requestAnimationFrame(() => {
        if (!textareaRef.current) return;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(before.length, before.length);
      });
      return;
    }
    const inserted = `/${skill.name} `;
    setVisibleDraft(before + inserted + after);
    setSlashOpen(false);
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const nextPos = before.length + inserted.length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(nextPos, nextPos);
    });
  }

  function pickTag(tag: SiteTag) {
    const el = textareaRef.current;
    if (!el || tagAnchor < 0) { setTagOpen(false); return; }
    const pos = el.selectionStart ?? 0;
    const before = draft.slice(0, tagAnchor);
    const after = draft.slice(pos).replace(/^[a-z0-9-]*/i, "");
    const inserted = `@${tag.handle} `;
    setVisibleDraft(before + inserted + after.replace(/^ /, ""));
    setTagOpen(false);
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const nextPos = before.length + inserted.length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(nextPos, nextPos);
    });
  }

  function removeTag(handle: string) {
    const next = draft.replace(new RegExp(`(^|[\\s([{,;:"'])@${handle}(?![a-z0-9-]) ?`, "gi"), "$1");
    setVisibleDraft(next);
    textareaRef.current?.focus({ preventScroll: true });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Confirming IME text is never a send action.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (tagOpen) {
      if (e.key === "Escape") { e.preventDefault(); setTagOpen(false); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setTagIndex((i) => Math.min(filteredTags.length - 1, i + 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setTagIndex((i) => Math.max(0, i - 1)); return; }
      if ((e.key === "Enter" && !e.shiftKey || e.key === "Tab") && filteredTags.length > 0) {
        e.preventDefault();
        pickTag(filteredTags[Math.max(0, Math.min(filteredTags.length - 1, tagIndex))]!);
        return;
      }
    }
    if (slashOpen) {
      if (e.key === "Escape") {
        e.preventDefault();
        setSlashOpen(false);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSlashSelectedIndex((i) =>
          Math.min(filteredSkills.length - 1, i + 1),
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSlashSelectedIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === "Enter" && !e.shiftKey && filteredSkills.length > 0) {
        e.preventDefault();
        pickSkill(
          filteredSkills[
            Math.max(
              0,
              Math.min(filteredSkills.length - 1, slashSelectedIndex),
            )
          ],
        );
        return;
      }
      if (e.key === "Tab" && filteredSkills.length > 0) {
        e.preventDefault();
        pickSkill(filteredSkills[Math.max(0, slashSelectedIndex)]);
        return;
      }
    }
    // Phone Return inserts a newline; the visible arrow remains the send action.
    if (e.key === "Enter" && !e.shiftKey && !mobileReturn) {
      e.preventDefault();
      if (adoptVisibleDraft() || canSend) void sendMessage();
    }
  }

  function onTextareaInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.currentTarget.value;
    const note = stripTypedClientProposalAlias(value);
    if (note !== null) {
      openClientProposal(note);
      return;
    }
    setVisibleDraft(value);
    updateSlashFromValue(value, e.currentTarget.selectionStart ?? value.length);
    updateTagFromValue(value, e.currentTarget.selectionStart ?? value.length);
  }

  function onAttachClick() {
    fileInputRef.current?.click();
  }
  async function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.currentTarget.files ?? []);
    e.currentTarget.value = "";
    for (const f of files) await addAttachment(f);
  }

  function onWhatsAppClick() {
    if (!activeCompanyId || useSession.getState().activeCompanyId !== activeCompanyId) return;
    setSlashOpen(false);
    waInputRef.current?.click();
  }
  function onWhatsAppFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.currentTarget.files?.[0] ?? null;
    e.currentTarget.value = "";
    if (f) setWaFile(f);
  }

  async function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = Array.from(e.clipboardData?.items ?? []);
    const files: File[] = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      for (const f of files) await addAttachment(f);
    }
  }
  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (e.dataTransfer.types.includes("Files")) setIsDropping(true);
  }
  function onDragLeave() {
    setIsDropping(false);
  }
  async function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDropping(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    for (const f of files) await addAttachment(f);
  }

  return (
    <form
      ref={formRef}
      className={`composerWrap ${isDropping ? "is-dropping" : ""}`}
      // Only the text field opens the bar: a focused button (Android/desktop
      // browsers focus on press) or a portalled sheet (Approvals) must not
      // reflow the bar in the middle of a tap.
      onFocus={(event) => { if ((event.target as Element) === textareaRef.current) setFocusInside(true); }}
      onBlur={onFormBlur}
      onPointerDown={() => { pointerInsideAt.current = Date.now(); }}
      onSubmit={(e) => {
        e.preventDefault();
        if (adoptVisibleDraft() || canSend) { void sendMessage(); settleAfterSend(); }
      }}
    >
      <SlashMenu
        open={slashOpen && !(slashAnchor === 0 && delegateDraft)}
        query={slashQuery}
        filteredSkills={filteredSkills}
        selectedIndex={slashSelectedIndex}
        onSelect={pickSkill}
        onHover={(i) => setSlashSelectedIndex(i)}
      />
      <TagMenu
        open={tagOpen && !slashOpen}
        query={tagQuery}
        tags={filteredTags}
        loading={siteTagsLoading}
        selectedIndex={tagIndex}
        onSelect={pickTag}
        onHover={(i) => setTagIndex(i)}
      />

      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={onFileInputChange}
        /* No `accept` filter on purpose. It used to be a MIME-only list, and
           macOS/Chrome pickers routinely fail to match the long Office MIME
           strings — which greyed out .docx/.xlsx/.pptx so they could not even
           be selected, while drag-drop and paste (which ignore `accept`) let
           the same file through. Leaving it open makes picker, drop and paste
           behave identically; the backend allow/deny-list is the real gate. */
      />

      {/* Separate input so the WhatsApp path can filter to .zip without
          affecting the general attach picker above. */}
      <input
        ref={waInputRef}
        type="file"
        accept=".zip,application/zip"
        style={{ display: "none" }}
        onChange={onWhatsAppFilePicked}
      />

      {waFile && (
        <WhatsAppImportSheet file={waFile} onClose={() => setWaFile(null)} />
      )}

      <div
        ref={barRef}
        className={`composer2 ${expanded ? "is-expanded" : ""} ${tall || expanded ? "has-expand" : ""} ${serverBusy ? "is-working" : ""} ${isDropping ? "is-drop-target" : ""} ${
          collapsed ? "is-collapsed" : ""} ${collapsed && armed ? "is-armed" : ""}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* Attention → Approve dock — built into the top of the input bar. */}
        <ApprovalBanner onReference={insertReference} onHandOver={(id, text) => void handOverAsExchange(id, text)} />
        <DelegateRunBadge />

        {attachments.length > 0 && (
          <div className="composer2__attachRow">
            {attachments.map((a) => (
              <AttachmentChip
                key={a.id}
                item={a}
                onRemove={removeAttachment}
                onPreview={(item) => setLightbox(item)}
                onHoverEnter={(item) => setHoverPreview(item)}
                onHoverLeave={() => setHoverPreview(null)}
                onEdit={(item) => { setHoverPreview(null); setAnnotating(item); }}
              />
            ))}
          </div>
        )}
        {annotating && (
          <ImageAnnotator
            item={annotating}
            onCancel={closeAnnotate}
            onSave={(file) => {
              const id = annotating.id;
              setAnnotating(null);
              void replaceAttachmentImage(id, file);
            }}
          />
        )}

        {proposalEditor && (
          <ClientProposalConfigurator
            mode="editing"
            options={proposalEditor}
            onChange={setProposalEditor}
            onCancel={cancelClientProposal}
            onConfirm={confirmClientProposal}
          />
        )}

        {(delegateDraft || lockedProposal || notifyOnDoneArmed) && (
          <div className="composer2__skillList" aria-label="Confirmed composer options">
            {delegateDraft && <span className="delegateBadge delegateBadge--draft" data-effort={delegateDraft.effort}
              title={delegateDraft.sampleProfile === "creative"
                ? "Creative sample for this message. Astra Max leads; parallel agents use High. Claude's setting is unchanged."
                : "Requested Astra effort for the next message only. Parallel agents use High. The normal Claude setting is unchanged."}>
              {delegateDraft.sampleProfile === "creative" ? <span>Astra · Creative</span> : <DelegateEffortPicker id="astra-draft-effort"
                aria-label="Astra main worker effort" value={delegateDraft.effort} disabled={delegateDraft.invalid}
                requestOpen={delegatePickerRequest} onDismiss={() => {
                  setDelegatePickerRequest(0);
                  setSlashOpen(false);
                  textareaRef.current?.focus();
                }}
                onChange={(effort) => setDelegateDraftEffort(useSession.getState(), activeCompanyId, "operations", effort)} />}
              <button type="button" className="delegateBadge__remove"
                aria-label="Remove Astra from draft" title="Use Claude for this draft; keep your text and attachments"
                onClick={() => clearDelegateDraft(useSession.getState(), activeCompanyId, "operations")}>×</button>
            </span>}
            {delegateDraft?.invalid && <span className="delegateBadge__error" role="alert">
              {delegateDraft.sampleProfile === "creative"
                ? "Creative chooses effort automatically. Remove the leading --option."
                : "Use --effort high, xhigh or max once, immediately after the command."}
            </span>}
            {creativeScopeInvalid && <span className="delegateBadge__error" role="alert">Open a Sample chat to use Creative.</span>}
            {lockedProposal && (
              <ClientProposalConfigurator
                mode="locked"
                options={lockedProposal.options}
                onCancel={cancelClientProposal}
                onEdit={editClientProposal}
              />
            )}
            {notifyOnDoneArmed && (
              <div className="clientProposalLock clientProposalLock--notify" aria-label="Notify on done is active">
                <div className="clientProposalLock__main">
                  <span className="clientProposalLock__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.75 21a2 2 0 0 1-3.5 0" />
                    </svg>
                  </span>
                  <span className="clientProposalLock__copy">
                    <span className="clientProposalLock__title">
                      Notify on done
                      <span className="clientProposalLock__check" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="m6.5 12.5 3.4 3.4 7.6-8" />
                        </svg>
                      </span>
                    </span>
                    <span className="clientProposalLock__summary">Telegram · When this reply finishes</span>
                  </span>
                  <span className="clientProposalLock__edit">Active</span>
                </div>
                <button
                  type="button"
                  className="clientProposalLock__remove"
                  onClick={() => setNotifyOnDoneArmed(false)}
                  aria-label="Remove notify on done"
                  title="Remove notify on done"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="m7 7 10 10M17 7 7 17" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        {proposalEditor && (
          <div className="clientProposal__messageLabel">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
            </svg>
            {draft.trim() ? "Your message is kept below" : "Add a message below (optional)"}
          </div>
        )}

        {(() => {
          // The sites tagged in this draft, as chips: favicon + name, × removes the tag.
          const handles = tagsInText(draft);
          const tagged = handles.map((h) => siteTags.find((t) => t.handle === h)).filter(Boolean) as SiteTag[];
          if (!tagged.length) return null;
          const recycle = /(^|\n)(?:\/[a-z0-9-]+[ \t]+)*\/recycle-sample(?=\s|$)/.test(draft);
          return (
            <div className="tagChips" aria-label="Tagged websites">
              {tagged.map((t) => (
                <span key={t.id} className="tagChip" title={`${tagDisplayName(t)} · ${t.host}`}>
                  <SiteFavicon tag={t} size={18} />
                  <span className="tagChip__name">{tagDisplayName(t)}</span>
                  <button type="button" className="tagChip__x" aria-label={`Remove @${t.handle}`}
                    onPointerDown={(e) => e.preventDefault()} onClick={() => removeTag(t.handle)}>×</button>
                </span>
              ))}
              {recycle && tagged.length > 2 && <span className="tagChips__warn">Recycle uses at most 2 sites. Remove one.</span>}
            </div>
          );
        })()}
        <div className={`reviewComposer ${reviewDraft.references.length ? "has-references" : ""}`}>
        {reviewDraft.references.length > 0 && <div className="reviewComposer__highlight composer2__input" ref={highlightRef} aria-hidden="true">
          <ReviewText text={draft + "\n"} references={reviewDraft.references} />
        </div>}
        {(tall || expanded) && (
          <button
            type="button"
            className="composer2__expand"
            aria-label={expanded ? "Shrink the message box" : "Expand the message box"}
            aria-pressed={expanded}
            // Keep the keyboard up: the tap must not take focus from the field.
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => { setExpanded((value) => !value); textareaRef.current?.focus({ preventScroll: true }); }}
          >
            {expanded ? <Minimize2 width={15} height={15} strokeWidth={1.75} /> : <Maximize2 width={15} height={15} strokeWidth={1.75} />}
          </button>
        )}
        <textarea
          ref={textareaRef}
          className={`composer2__input ${proposalEditor ? "is-proposal-note" : ""}`}
          placeholder={
            proposalEditor
              ? "Add context for the proposal (optional)"
              : activeCompanyId
              ? "Ask anything"
              : "Pick a company in the sidebar to start"
          }
          value={draft}
          onChange={onTextareaInput}
          onKeyDown={onKeyDown}
          onKeyUp={recomputeSlash}
          onFocus={(e) => {
            recomputeSlash();
            // PWA on iOS: when the keyboard opens, the composer can
            // disappear behind it for a frame. Scroll it back into view
            // once iOS has had time to lay out the resized viewport.
            // 320ms covers the standard keyboard slide-up animation.
            if (!isPhone) {
              window.setTimeout(() => {
                e.target.scrollIntoView({ block: "end", behavior: "smooth" });
              }, 320);
              return;
            }
            // Phones too, like Build (29 Sep): iOS does NOT reliably pan the
            // installed app on its own, so without this the bar stayed hidden
            // behind the keyboard. The page slides up only as far as the bar
            // (header scrolls out, the chat above stays visible); the settle
            // effect above puts the page back when the keyboard closes.
            kbFollowRef.current?.();
            const field = e.target;
            // The whole bar (text + Tools row), not just the text field, lands on the keyboard.
            const bar = () => (field.closest("form") ?? field) as HTMLElement;
            const reveal = () => {
              if (document.activeElement === field) bar().scrollIntoView({ block: "end", behavior: "smooth" });
            };
            const first = window.setTimeout(reveal, 320);
            // A slower keyboard (first open, predictive bar, hardware lag): once
            // the visible area has settled, bring the bar up if it is still hidden.
            const vv = window.visualViewport;
            let timer = 0;
            const check = () => {
              window.clearTimeout(timer);
              timer = window.setTimeout(() => {
                if (vv && bar().getBoundingClientRect().bottom > vv.offsetTop + vv.height + 1) reveal();
              }, 120);
            };
            const stop = () => {
              window.clearTimeout(first);
              window.clearTimeout(timer);
              vv?.removeEventListener("resize", check);
              field.removeEventListener("blur", stop);
              if (kbFollowRef.current === stop) kbFollowRef.current = null;
            };
            vv?.addEventListener("resize", check);
            field.addEventListener("blur", stop);
            kbFollowRef.current = stop;
          }}
          // Open the bar on the tap's click, never on its pointer-down: a
          // reflow between touch-start and touch-end made iOS drop the focus,
          // so the first tap did nothing and only a second tap brought up the
          // keyboard. The click also reopens it if it was closed while focused.
          onClick={() => { recomputeSlash(); if (isPhone) setFocusInside(true); }}
          onPaste={onPaste}
          onScroll={(event) => {
            if (highlightRef.current) highlightRef.current.scrollTop = event.currentTarget.scrollTop;
          }}
          /* NO maxLength — Julian hit the 4000-char cap pasting long briefs/chat logs.
             The backend has no text limit; this was a UI-only ceiling. (2026-08-02) */
          rows={1}
          disabled={!activeCompanyId}
          // iOS / mobile keyboard polish:
          //  - enterkeyhint=enter → phone keyboard offers Return/newline.
          //    Desktop Enter-to-send and Shift+Enter remain unchanged.
          //  - autoCorrect=on     → real autocorrect for natural typing
          //    (chat input, not a code field — was off before).
          //  - autoCapitalize=sentences → first letter of each sentence caps.
          //  - inputMode=text     → standard alphanumeric keyboard, no emoji
          //    panel jamming up the layout.
          //  - spellCheck=true    → red squiggles where the OS catches typos.
          spellCheck={true}
          autoComplete="off"
          autoCorrect="on"
          autoCapitalize="sentences"
          enterKeyHint="enter"
          inputMode="text"
        />
        </div>

        <div className="composer2__bar composer2__bar--workflows">
          <div className="composer2__left composer2__left--workflows">
            <button
              type="button"
              className="composer2__attach"
              onClick={onAttachClick}
              aria-label="Attach files"
              title="Attach files"
              disabled={!activeCompanyId}
            >
              <Plus width={16} height={16} />
            </button>
            <WorkflowPicker
              onInsert={insertWorkflow}
              getCurrentDraft={getWorkflowDraft}
              skills={filterSkills("")}
              authFetch={authFetch}
              disabled={!activeCompanyId}
              contextKey={activeCompanyId ?? "no-company"}
              renderTrigger={({ onClick, disabled, buttonRef }) => (
                <ComposerTools disabled={disabled} triggerRef={buttonRef}
                  onWorkflows={onClick} onSchedules={openScheduledTools}
                  onWhatsApp={WHATSAPP_IMPORT_ENABLED ? onWhatsAppClick : undefined} />
              )}
            />
            <button
              type="button"
              className={`composer2__pill is-active composer2__pill--${thinkingLevel} ${thinkMenuOpen ? "is-open" : ""}`}
              onClick={(e) => { const chip = e.currentTarget; setThinkAnchor((open) => (open ? null : chip)); }}
              disabled={!activeCompanyId}
              aria-haspopup="dialog"
              aria-expanded={thinkMenuOpen}
              aria-label={`Thinking level: ${EFFORT_LABEL[thinkingLevel]}. Choose a level.`}
              title={`Thinking: ${EFFORT_LABEL[thinkingLevel]}`}
            >
              <svg
                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
                width="13" height="13" aria-hidden="true"
              >
                <path d="M12 3l1.6 4.6L18 9l-4.4 1.4L12 15l-1.6-4.6L6 9l4.4-1.4L12 3z" />
                <path d="M18.5 14.5l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" />
              </svg>
              <span className="composer2__pillLabel">{EFFORT_LABEL[thinkingLevel]}</span>
              <svg className="composer2__pillChevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" width="12" height="12" aria-hidden="true"><polyline points="6 15 12 9 18 15" /></svg>
            </button>
            {thinkMenuOpen && (
              <ThinkingLevelMenu current={thinkingLevel} anchor={thinkAnchor}
                onPick={(level) => setThinkingLevel(level)} onClose={closeThinkMenu} />
            )}
            {queueLength > 0 && (
              <span
                className="composer2__queueChip"
                title="Messages pending — see delivery status above"
              >
                {queueLength} pending
              </span>
            )}
          </div>

          <div className="composer2__right">
            <ContextMeter chatId={activeCompanyId} isStreaming={isStreaming} />
            {isStreaming && (
              <button
                type="button"
                className="composer2__send composer2__send--stop"
                onClick={() => void interruptStream()}
                aria-label="Stop current reply"
                title="Stop current reply"
              >
                <span className="composer2__stopIcon" aria-hidden="true" />
              </button>
            )}
            <button
              type={isStreaming ? "button" : "submit"}
              className="composer2__send"
              onClick={() => {
                if (isStreaming && canSend) { void sendMessage(); settleAfterSend(); }
              }}
              aria-label={isStreaming ? "Queue another message" : "Send message"}
              title={isStreaming ? "Queue another message" : "Send"}
              disabled={!canSend}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" style={{ transform: "translateY(-1px)" }}>
                <line x1="12" y1="22" x2="12" y2="3" />
                <polyline points="4 11 12 3 20 11" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
